import { supabase } from "../../lib/supabase";
import { UserAddress } from "../../types";
import { saveActionLog } from "./systemService";

export const fetchFighterTagsForUser = async (userId: string | number) => {
  try {
    let dbUserId = userId;
    if (typeof userId === 'string' && (userId.includes('-') || userId.includes('@') || isNaN(Number(userId)))) {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
      let query = supabase.from('users').select('id');
      if (isUUID) {
        query = query.eq('auth_id', userId);
      } else if (userId.includes('@')) {
        query = query.eq('email_copy', userId);
      } else {
        query = query.eq('username', userId);
      }
      const { data: userRow } = await query.maybeSingle();
      if (userRow) {
        dbUserId = userRow.id;
      } else {
        return [];
      }
    }

    const { data: userTags, error } = await supabase
      .from('user_fighter_tags')
      .select('fighter_tags(name)')
      .eq('user_id', dbUserId);

    if (error) {
      console.error('Error fetching user fighter tags:', error);
      return [];
    }

    return (userTags || []).map((ut: any) => ut.fighter_tags?.name || ut.tag?.name).filter(Boolean);
  } catch (err) {
    console.error('Exception in fetchFighterTagsForUser:', err);
    return [];
  }
};


export const getUserProfile = async (userId: string | number) => {
  try {
    // Determine if we should search by ID, Email, or Username
    const isNumeric = !isNaN(Number(userId)) && !String(userId).includes('@') && !String(userId).includes('-');
    
    let query = (supabase.rpc('get_safe_users') as any).select('*');
    
    if (isNumeric) {
      const { data, error } = await query
        .eq('id', userId)
        .limit(1);
      
      if (data && data.length > 0) {
        const u = data[0];
        const fighter_tags = await fetchFighterTagsForUser(u.id);
        return { ...u, role: (u.role as any)?.name || 'user', fighter_tags };
      }
      if (error) throw error;
    }

    // Try finding by username or other fields in safe_users
    const { data: byUsername, error: usernameError } = await query
      .eq('username', userId)
      .maybeSingle();
    
    if (byUsername) {
      const fighter_tags = await fetchFighterTagsForUser(byUsername.id);
      return { ...byUsername, role: (byUsername.role as any)?.name || 'user', fighter_tags };
    }

    // If still not found, try finding by email in USERS table (private)
    if (String(userId).includes('@')) {
      const { data: byEmail, error: emailError } = await supabase
        .from('users')
        .select('*')
        .eq('email_copy', userId)
        .maybeSingle();
      
      if (byEmail) {
        const fighter_tags = await fetchFighterTagsForUser(byEmail.id);
        return { ...byEmail, role: (byEmail.role as any)?.name || 'user', fighter_tags };
      }
    }

    // Try finding by auth_id (UUID)
    if (String(userId).length > 30) {
      const { data, error } = await query
        .eq('auth_id', userId)
        .limit(1);
      
      if (data && data.length > 0) {
        const u = data[0];
        const fighter_tags = await fetchFighterTagsForUser(u.id);
        return { ...u, role: (u.role as any)?.name || 'user', fighter_tags };
      }
      if (error) throw error;
    }

    // Finally try finding by username
    const { data, error } = await query
      .eq('username', userId)
      .limit(1);
    
    if (error) throw error;
    
    const user = (data && data.length > 0) ? data[0] : null;
    if (user) {
      const fighter_tags = await fetchFighterTagsForUser(user.id);
      return { ...user, role: (user.role as any)?.name || 'user', fighter_tags };
    }
    
    console.warn(`User profile not found for identifier: ${userId}`);
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};


export const getUserByAuthId = async (authId: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*, roles(name)')
      .eq('auth_id', authId)
      .maybeSingle();

    if (error) throw error;
    if (data) {
      const roleName = Array.isArray(data.roles) 
        ? (data.roles[0] as any)?.name 
        : (data.roles as any)?.name;
      const fighter_tags = await fetchFighterTagsForUser(data.id);
      return { ...data, role: roleName || 'user', fighter_tags };
    }
    return data;
  } catch (error) {
    console.error('Error in getUserByAuthId:', error);
    return null;
  }
};


export const isAdmin = async (authId: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('roles(name)')
      .eq('auth_id', authId)
      .maybeSingle();
    
    if (error) return false;
    const roleName = Array.isArray(data?.roles) 
      ? (data?.roles[0] as any)?.name 
      : (data?.roles as any)?.name;
    return roleName === 'admin';
  } catch {
    return false;
  }
};

const getAuthHeaders = async (contentType = 'application/json') => {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: any = {
        'Content-Type': contentType
    };
    
    if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Dev impersonation support
    const impersonated = localStorage.getItem('cardumy_impersonated_user');
    if (impersonated) {
        const user = JSON.parse(impersonated);
        headers['X-Impersonate-User-Id'] = user.id;
    }

    return headers;
};


export const getPaginatedUsers = async (page: number, limit: number = 20) => {
  try {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await (supabase
      .rpc('get_safe_users') as any)
      .select('*', { count: 'exact' })
      .order('username', { ascending: true })
      .range(from, to);
    
    if (error) throw error;

    const usersData = (data || []) as any[];
    const userIds = usersData.map((u: any) => u.id);

    // Fetch store logos separately via store_members
    const { data: storesData } = await supabase
      .from('stores')
      .select('logo, store_members!inner(user_id)')
      .in('store_members.user_id', userIds);

    const storeMap = (storesData || []).reduce((acc: any, s: any) => {
      const member = Array.isArray(s.store_members) ? s.store_members[0] : s.store_members;
      if (member?.user_id) acc[member.user_id] = s.logo;
      return acc;
    }, {});

    // Fetch followers counts for these users from user_followers
    let followersMap: Record<string | number, number> = {};
    if (userIds.length > 0) {
      const { data: followersData } = await supabase
        .from('user_followers')
        .select('followed_id')
        .in('followed_id', userIds);

      (followersData || []).forEach((f: any) => {
        if (f.followed_id) {
          followersMap[f.followed_id] = (followersMap[f.followed_id] || 0) + 1;
        }
      });
    }
    
    const users = usersData.map((u: any) => ({
      ...u,
      store_logo: storeMap[u.id] || null,
      followers_count: followersMap[u.id] || 0
    }));

    return { users, total: count || 0 };
  } catch (error) {
    console.error('Error fetching paginated users:', error);
    return { users: [], total: 0 };
  }
};


/**
 * Busca a lista segura de usuários públicos usando a RPC segura `get_safe_users`
 * Respeita RLS e retorna dados essenciais de perfil e contadores de cartas
 */
export const getSafeUsers = async (): Promise<any[]> => {
  try {
    const { data, error } = await (supabase.rpc('get_safe_users') as any);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching safe users via get_safe_users RPC:', error);
    return [];
  }
};

export const getAllUsers = async () => {
  try {
    // Tenta primeiro via RPC segura get_safe_users
    const { data: safeUsers, error: rpcError } = await (supabase.rpc('get_safe_users') as any);
    if (!rpcError && safeUsers && safeUsers.length > 0) {
      return safeUsers;
    }

    const { data, error } = await supabase
      .from('users')
      .select('*, store_members(store:stores(logo))')
      .order('username', { ascending: true });
    
    if (error) throw error;
    return (data || []).map(u => ({
      ...u,
      store_logo: u.store_members?.[0]?.store?.logo
    }));
  } catch (error) {
    console.error('Error fetching all users from DB:', error);
    return [];
  }
};


export const getRandomUser = async () => {
  try {
    // Attempt to get a random user from the database
    // We fetch a larger pool and pick one to avoid complex random SQL logic in client
    const { data, error } = await supabase
      .from('users')
      .select('*, store_members(store:stores(logo))')
      .limit(20);
    
    if (error) throw error;
    if (data && data.length > 0) {
      const u = data[Math.floor(Math.random() * data.length)];
      return {
        ...u,
        store_logo: u.store_members?.[0]?.store?.logo
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching random user from DB:', error);
    return null;
  }
};


export const syncUser = async (profile: { auth_id: string; email: string; displayName?: string }) => {
  try {
    // 1. Check if user already exists by auth_id (using existing function for safety)
    const existing = await getUserByAuthId(profile.auth_id);
    if (existing) return existing;

    // 2. Check if user exists by email (legacy link)
    const { data: byEmail } = await supabase
      .from('users')
      .select('*')
      .eq('email_copy', profile.email)
      .maybeSingle();

    if (byEmail && !byEmail.auth_id) {
      // Update legacy user with new auth_id
      const { data: updated, error: updateError } = await supabase
        .from('users')
        .update({ auth_id: profile.auth_id })
        .eq('id', byEmail.id)
        .select()
        .single();
      
      if (!updateError) return updated;
    }

    // 3. Create new user
    const username = profile.displayName || profile.email.split('@')[0] + '_' + Math.random().toString(36).slice(2, 7);
    
    const { data: inserted, error: insertError } = await supabase
      .from('users')
      .insert({
        auth_id: profile.auth_id,
        email_copy: profile.email,
        username: username,
        avatar: 'https://api.dicebear.com/7.x/pixel-art/svg?seed=' + profile.auth_id
      })
      .select()
      .single();

    if (insertError) throw insertError;

    if (inserted) {
      const { data: tagData } = await supabase
        .from('fighter_tags')
        .select('id')
        .ilike('slug', 'novato')
        .maybeSingle();

      if (tagData) {
        await supabase
          .from('user_fighter_tags')
          .insert({
            user_id: inserted.id,
            tag_id: tagData.id
          });
      }
    }

    return inserted;
  } catch (error) {
    console.error('Error in syncUser:', error);
    return null;
  }
};


export const updateUserProfile = async (userId: string | number, updates: any) => {
  try {
    const { fighter_tags, ...rawUpdates } = updates;

    const sanitizedUpdates: Record<string, any> = {};

    // 1. Fullname
    if (rawUpdates.fullname !== undefined) {
      sanitizedUpdates.fullname = String(rawUpdates.fullname).trim();
    }

    // 2. Username
    if (rawUpdates.username !== undefined) {
      sanitizedUpdates.username = String(rawUpdates.username).trim();
    }

    // 3. Bio
    if (rawUpdates.bio !== undefined) {
      const trimmedBio = String(rawUpdates.bio).trim();
      sanitizedUpdates.bio = trimmedBio.length > 0 ? trimmedBio : null;
    }

    // 4. Phone
    if (rawUpdates.phone !== undefined) {
      const trimmedPhone = String(rawUpdates.phone).trim();
      sanitizedUpdates.phone = trimmedPhone.length > 0 ? trimmedPhone.slice(0, 20) : null;
    }

    // 5. Birth Date (Must respect chk_minimum_age check)
    if (rawUpdates.birth_date !== undefined) {
      const trimmedDate = String(rawUpdates.birth_date).trim();
      sanitizedUpdates.birth_date = trimmedDate.length > 0 ? trimmedDate : null;
    }

    // 6. Gender
    if (rawUpdates.gender !== undefined) {
      const trimmedGender = String(rawUpdates.gender).trim();
      sanitizedUpdates.gender = trimmedGender.length > 0 ? trimmedGender : null;
    }

    // 7. Favorite Game ID (fav_game_id)
    const favGameVal = rawUpdates.fav_game_id !== undefined ? rawUpdates.fav_game_id : rawUpdates.favorite_cardgame_id;
    if (favGameVal !== undefined) {
      if (favGameVal === "" || favGameVal === null || favGameVal === "null") {
        sanitizedUpdates.fav_game_id = null;
      } else {
        const parsedGameId = Number(favGameVal);
        sanitizedUpdates.fav_game_id = !isNaN(parsedGameId) && parsedGameId > 0 ? parsedGameId : null;
      }
    }

    // 8. Avatar
    if (rawUpdates.avatar !== undefined) {
      const trimmedAvatar = String(rawUpdates.avatar).trim();
      sanitizedUpdates.avatar = trimmedAvatar.length > 0 ? trimmedAvatar : null;
    }

    // 9. Banner / Cover URL
    const bannerVal = rawUpdates.banner_url !== undefined ? rawUpdates.banner_url : rawUpdates.cover_url;
    if (bannerVal !== undefined) {
      const trimmedBanner = String(bannerVal).trim();
      sanitizedUpdates.banner_url = trimmedBanner.length > 0 ? trimmedBanner : null;
    }

    // 10. Bandai Member (bandai_member)
    if (rawUpdates.bandai_member !== undefined) {
      const trimmedBandai = String(rawUpdates.bandai_member).trim();
      sanitizedUpdates.bandai_member = trimmedBandai.length > 0 ? trimmedBandai : null;
    }

    // 11. Updated At
    sanitizedUpdates.updated_at = new Date().toISOString();

    // Identify user in database
    const isUUID = typeof userId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
    const isNumeric = !isNaN(Number(userId)) && !String(userId).includes('@') && !String(userId).includes('-');

    let matchColumn = 'id';
    let matchValue: any = userId;

    if (isNumeric) {
      matchColumn = 'id';
      matchValue = Number(userId);
    } else if (isUUID) {
      matchColumn = 'auth_id';
      matchValue = userId;
    } else if (String(userId).includes('@')) {
      matchColumn = 'email_copy';
      matchValue = userId;
    } else {
      matchColumn = 'username';
      matchValue = userId;
    }

    const { error: updateError } = await supabase
      .from('users')
      .update(sanitizedUpdates)
      .eq(matchColumn, matchValue);
    
    if (updateError) {
      console.error('Error updating users table:', updateError);
      throw updateError;
    }

    if (fighter_tags !== undefined) {
      let dbUserId: number | null = null;
      if (isNumeric) {
        dbUserId = Number(userId);
      } else {
        const { data: userRow } = await supabase
          .from('users')
          .select('id')
          .eq(matchColumn, matchValue)
          .maybeSingle();
        if (userRow) {
          dbUserId = userRow.id;
        }
      }

      if (dbUserId) {
        await supabase
          .from('user_fighter_tags')
          .delete()
          .eq('user_id', dbUserId);

        const tagsArray = Array.isArray(fighter_tags) 
          ? fighter_tags 
          : typeof fighter_tags === 'string' 
            ? fighter_tags.split(',').map((t: string) => t.trim()).filter((t: string) => t !== '')
            : [];

        if (tagsArray.length > 0) {
          const { data: allTags } = await supabase
            .from('fighter_tags')
            .select('id, name');

          if (allTags && allTags.length > 0) {
            const tagMap = new Map<string, number>();
            allTags.forEach(t => {
              tagMap.set(t.name.toLowerCase().trim(), t.id);
            });

            const inserts = tagsArray
              .map(tName => {
                const tagId = tagMap.get(tName.toLowerCase().trim());
                return tagId ? { user_id: dbUserId, tag_id: tagId } : null;
              })
              .filter(Boolean) as { user_id: number; tag_id: number }[];

            if (inserts.length > 0) {
              const { error: insertError } = await supabase
                .from('user_fighter_tags')
                .insert(inserts);
              if (insertError) {
                console.error('Error inserting user fighter tags:', insertError);
              }
            }
          }
        }
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error updating profile with Supabase directly:', error);
    return { success: false, error: error?.message || 'Erro ao atualizar perfil' };
  }
};


export const getFullUserProfile = async (username: string, followerId?: string) => {
  if (!username || username === 'undefined' || username === 'null' || !String(username).trim()) {
    return null;
  }

  try {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username);
    const isInteger = /^\d+$/.test(username);
    const isEmail = username.includes('@');

    let summaryData: any = null;

    if (!isUUID && !isInteger && !isEmail) {
      // Clean username: call get_user_profile_summary_by_username RPC
      try {
        const { data, error } = await supabase.rpc('get_user_profile_summary_by_username', { p_username: username });
        if (!error && data) {
          summaryData = data;
        } else if (error) {
          console.debug('get_user_profile_summary_by_username notice:', error.message || error);
        }
      } catch (err: any) {
        console.debug('get_user_profile_summary_by_username fetch notice:', err?.message || err);
      }
    } else if (isInteger) {
      // User ID: call get_user_profile_summary RPC
      try {
        const { data, error } = await supabase.rpc('get_user_profile_summary', { p_user_id: Number(username) });
        if (!error && data) {
          summaryData = data;
        } else if (error) {
          console.debug('get_user_profile_summary notice:', error.message || error);
        }
      } catch (err: any) {
        console.debug('get_user_profile_summary fetch notice:', err?.message || err);
      }
    }

    // Fallback if we do not have summaryData (e.g. if looking up via auth UUID or Email)
    let resolvedUserId: any = null;
    if (summaryData) {
      resolvedUserId = summaryData.id;
    } else {
      if (isUUID) {
        const { data: userRaw } = await supabase.from('users').select('id').eq('auth_id', username).maybeSingle();
        if (userRaw) resolvedUserId = userRaw.id;
      } else if (isEmail) {
        const { data: userRaw } = await supabase.from('users').select('id').eq('email_copy', username).maybeSingle();
        if (userRaw) resolvedUserId = userRaw.id;
      } else {
        const { data: userRaw } = await supabase.from('users').select('id').eq('username', username).maybeSingle();
        if (userRaw) resolvedUserId = userRaw.id;
      }

      if (resolvedUserId) {
        try {
          const { data, error } = await supabase.rpc('get_user_profile_summary', { p_user_id: Number(resolvedUserId) });
          if (!error && data) {
            summaryData = data;
          }
        } catch (err: any) {
          console.debug('get_user_profile_summary fallback fetch notice:', err?.message || err);
        }
      }
    }

    if (!summaryData) {
      return null;
    }

    resolvedUserId = summaryData.id;

    // Fetch store separately to avoid metadata issue with views
    const { data: storeData } = await supabase
      .from('stores')
      .select('*, store_members!inner(user_id)')
      .eq('store_members.user_id', resolvedUserId)
      .maybeSingle();
    
    const stores = storeData ? [storeData] : [];

    // Parse statistics from RPC
    const rpcStats = summaryData.stats || {};
    const collection_size = Number(rpcStats.collection_cards || 0);
    const wishlist_size = Number(rpcStats.wishlist_cards || 0);
    const offers_size = Number(rpcStats.offerlist_cards || 0);
    const followersCount = Number(rpcStats.followers || 0);
    const followingCount = Number(rpcStats.following || 0);

    // Fetch public/private attributes for likes/dislikes (fallback if missing in RPC)
    let extraUserFields: any = {};
    try {
      const { data: safeUser } = await (supabase.rpc('get_safe_users') as any)
        .select('*')
        .eq('id', resolvedUserId)
        .maybeSingle();
      if (safeUser) {
        extraUserFields = safeUser;
      }
    } catch {
      // Ignore if view/rpc has restrictions
    }

    let privateUserFields: any = {};
    try {
      const { data: privateUser } = await supabase
        .from('users')
        .select('*')
        .eq('id', resolvedUserId)
        .maybeSingle();
      if (privateUser) {
        privateUserFields = privateUser;
      }
    } catch {
      // Ignore RLS block
    }

    const likes = Number(extraUserFields.likes || privateUserFields.likes || 0);
    const dislikes = Number(extraUserFields.dislikes || privateUserFields.dislikes || 0);

    // Fetch user reviews
    const reviewsCountQuery = supabase.from('user_reviews').select('*', { count: 'exact', head: true }).eq('reviewed_id', resolvedUserId);
    const reviewsQuery = supabase
      .from('user_reviews')
      .select('*')
      .eq('reviewed_id', resolvedUserId)
      .order('created_at', { ascending: false })
      .limit(10);

    const results = await Promise.all([
      reviewsCountQuery,
      reviewsQuery
    ]);

    const totalReviewsCount = results[0]?.count;
    let reviewsData = results[1]?.data || [];
    
    if (results[1]?.error) {
      console.error('Error fetching reviews:', results[1].error);
    }

    if (reviewsData.length > 0) {
      const reviewerIds = Array.from(new Set(reviewsData.map((r: any) => r.reviewer_id).filter(Boolean)));
      if (reviewerIds.length > 0) {
        try {
          const { data: safeReviewers } = await (supabase.rpc('get_safe_users') as any)
            .select('id, username, fullname, avatar')
            .in('id', reviewerIds);
          
          if (safeReviewers && safeReviewers.length > 0) {
            const reviewerMap = safeReviewers.reduce((acc: any, curr: any) => {
              acc[curr.id] = curr;
              return acc;
            }, {});
            
            reviewsData = reviewsData.map((r: any) => ({
              ...r,
              reviewer: reviewerMap[r.reviewer_id] || null
            }));
          }
        } catch (err) {
          console.warn('Error fetching safe reviewers for profile reviews UI:', err);
        }
      }
    }

    const total_reviews = totalReviewsCount !== null && totalReviewsCount !== undefined ? totalReviewsCount : (likes + dislikes);
    const approval_rate = (likes + dislikes) > 0 ? Math.round((likes / (likes + dislikes)) * 100) : 100;

    let isFollowing = false;
    if (followerId) {
       const { data: followData } = await supabase.from('user_followers').select('*').eq('followed_id', resolvedUserId).eq('follower_id', followerId || '').maybeSingle();
       isFollowing = !!followData;
    }

    const fighter_tags = await fetchFighterTagsForUser(resolvedUserId);

    return {
      user: {
        id: resolvedUserId,
        username: summaryData.username || extraUserFields.username || privateUserFields.username,
        fullname: summaryData.fullname || extraUserFields.fullname || privateUserFields.fullname,
        avatar: summaryData.avatar || extraUserFields.avatar || privateUserFields.avatar,
        bio: summaryData.bio || extraUserFields.bio || privateUserFields.bio,
        created_at: summaryData.member_since || extraUserFields.created_at || privateUserFields.created_at,
        banner_url: extraUserFields.banner_url || privateUserFields.banner_url || null,
        email_copy: privateUserFields.email_copy || null,
        role_id: extraUserFields.role_id || privateUserFields.role_id || null,
        stores,
        store_logo: storeData?.logo || null,
        fighter_tags
      },
      stats: {
        collection_size,
        wishlist_size,
        offers_size,
        followers: followersCount,
        following: followingCount,
        approval_rate,
        total_reviews,
        likes,
        dislikes
      },
      reviews: reviewsData || [],
      isFollowing
    };
  } catch (error) {
    console.error('Error fetching full user profile:', error);
    return null;
  }
};


export const getUserProfileSummary = async (userId: number | string) => {
  try {
    const { data, error } = await supabase.rpc('get_user_profile_summary', { p_user_id: Number(userId) });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error in getUserProfileSummary:', error);
    return null;
  }
};


export const getUserProfileSummaryByUsername = async (username: string) => {
  if (!username || username === 'undefined' || username === 'null' || !String(username).trim()) {
    return null;
  }
  try {
    const { data, error } = await supabase.rpc('get_user_profile_summary_by_username', { p_username: username });
    if (error) {
      console.debug('getUserProfileSummaryByUsername notice:', error.message || error);
      return null;
    }
    return data;
  } catch (error: any) {
    console.debug('getUserProfileSummaryByUsername fetch notice:', error?.message || error);
    return null;
  }
};

const resolveUserIdByUsername = async (username: string): Promise<number> => {
  if (!username || username === 'undefined' || username === 'null' || !String(username).trim()) {
    return 0;
  }
  try {
    const { data: summaryData, error } = await supabase.rpc('get_user_profile_summary_by_username', { p_username: username });
    if (!error && summaryData?.id) {
      return Number(summaryData.id);
    }
  } catch (e: any) {
    console.debug('RPC resolution notice:', e?.message || e);
  }

  try {
    const { data: safeUser } = await (supabase.rpc('get_safe_users') as any)
      .select('id')
      .eq('username', username)
      .maybeSingle();
    if (safeUser?.id) {
      return Number(safeUser.id);
    }
  } catch (e: any) {
    console.debug('Safe users RPC resolution notice:', e?.message || e);
  }

  const { data: user } = await supabase.from('users').select('id').eq('username', username).maybeSingle();
  if (user?.id) {
    return Number(user.id);
  }

  throw new Error('User not found');
};


export const followUser = async (username: string, followerId: string) => {
  try {
    const targetUserId = await resolveUserIdByUsername(username);

    const { error } = await supabase.from('user_followers').insert({
        followed_id: targetUserId,
        follower_id: followerId
    });

    if (error) throw error;

    // Log action
    await saveActionLog('follow_user', 'users', String(targetUserId), { 
      username, 
      message: `começou a seguir ${username}` 
    });

    return { success: true };
  } catch (error) {
    console.error('Error following user:', error);
    return null;
  }
};


export const unfollowUser = async (username: string, followerId: string) => {
  try {
    const targetUserId = await resolveUserIdByUsername(username);

    const { error } = await supabase.from('user_followers').delete().eq('followed_id', targetUserId).eq('follower_id', followerId);

    if (error) throw error;

    // Log action
    await saveActionLog('unfollow_user', 'users', String(targetUserId), { 
      username, 
      message: `parou de seguir ${username}` 
    });

    return { success: true };
  } catch (error) {
    console.error('Error unfollowing user:', error);
    return null;
  }
};


export const submitUserReview = async (username: string, reviewerId: string, isPositive: boolean, comment: string) => {
  try {
    const targetUserId = await resolveUserIdByUsername(username);

    const { error } = await supabase.from('user_reviews').insert({
        reviewed_id: targetUserId,
        reviewer_id: reviewerId,
        is_positive: isPositive,
        comment
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error submitting review:', error);
    return null;
  }
};


export const searchUsers = async (searchTerm: string) => {
  try {
    const clean = searchTerm ? searchTerm.trim().replace(/^@/, '') : '';
    let query = (supabase.rpc('get_safe_users', { p_limit: 50, p_offset: 0 }) as any)
      .select('id, username, fullname, avatar, vip');
    
    if (clean) {
      query = query.or(`username.ilike.%${clean}%,fullname.ilike.%${clean}%`);
    }

    const { data, error } = await query.limit(20);
    
    if (error) {
      console.warn('get_safe_users error in searchUsers:', error);
      // Fallback
      let fallbackQuery = supabase
        .from('users')
        .select('id, username, fullname, avatar, vip');
      if (clean) {
        fallbackQuery = fallbackQuery.or(`username.ilike.%${clean}%,fullname.ilike.%${clean}%`);
      }
      const { data: fallback, error: fallbackError } = await fallbackQuery.limit(20);
      if (fallbackError) {
        console.warn('Fallback users query error in searchUsers:', fallbackError);
        return [];
      }
      return fallback || [];
    }
    return data || [];
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};


export const getAddresses = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return [];

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) return [];

        const { data, error } = await supabase
            .from('user_addresses')
            .select('*')
            .eq('user_id', profile.id);
            
        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching addresses:', error);
        return [];
    }
};


export const createAddress = async (address: Partial<UserAddress>) => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error('Not authenticated');

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) throw new Error('Profile not found');

        // If this is set as default, unset others first
        if (address.is_default) {
            await supabase
                .from('user_addresses')
                .update({ is_default: false })
                .eq('user_id', profile.id);
        }

        const { data, error } = await supabase
            .from('user_addresses')
            .insert({
                ...address,
                user_id: profile.id
            })
            .select()
            .maybeSingle();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error creating address:', error);
        throw error;
    }
};


export const updateAddress = async (addressId: string | number, address: Partial<UserAddress>) => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error('Not authenticated');

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) throw new Error('Profile not found');

        if (address.is_default) {
            await supabase
                .from('user_addresses')
                .update({ is_default: false })
                .eq('user_id', profile.id)
                .neq('id', addressId);
        }

        const { data, error } = await supabase
            .from('user_addresses')
            .update(address)
            .eq('id', addressId)
            .eq('user_id', profile.id) // Security check
            .select()
            .maybeSingle();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating address:', error);
        throw error;
    }
};


export const deleteAddress = async (addressId: string | number) => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error('Not authenticated');

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) throw new Error('Profile not found');

        const { error } = await supabase
            .from('user_addresses')
            .delete()
            .eq('id', addressId)
            .eq('user_id', profile.id);

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error deleting address:', error);
        throw error;
    }
};


export const getPrimaryAddress = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return null;

    const { data, error } = await supabase
      .from('user_addresses')
      .select('*')
      .eq('user_id', profile.id)
      .eq('is_default', true)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching primary address:', error);
    return null;
  }
};


export const checkUserDocument = async (userId: number) => {
  const { data, error } = await supabase
    .from('user_documents')
    .select('id, cpf_last4')
    .eq('user_id', userId)
    .maybeSingle();
  
  if (error) return null;
  return data;
};


export const saveUserDocument = async (userId: number, cpf: string) => {
  // Simple "encryption" for demo/structure purposes
  const cpf_last4 = cpf.slice(-4);
  const cpf_encrypted = btoa(cpf); // Simple base64 for now, usually handled by vault/backend

  const { data, error } = await supabase
    .from('user_documents')
    .upsert({
      user_id: userId,
      cpf_last4,
      cpf_encrypted,
      updated_at: new Date().toISOString()
    }, { onConflict: 'user_id' })
    .select()
    .single();

  if (error) throw error;
  return data;
};

const resolveTargetId = async (idOrUsername: string | number): Promise<number> => {
  if (!idOrUsername) return 0;
  if (typeof idOrUsername === 'number' && !isNaN(idOrUsername)) return idOrUsername;
  const parsed = Number(idOrUsername);
  if (!isNaN(parsed) && String(parsed) === String(idOrUsername).trim()) return parsed;
  return resolveUserIdByUsername(String(idOrUsername));
};

/**
 * Retorna os seguidores de um usuário (quem segue o usuário)
 */
export const getUserFollowers = async (idOrUsername: string | number): Promise<any[]> => {
  try {
    const targetUserId = await resolveTargetId(idOrUsername);
    if (!targetUserId) return [];

    const { data: followRows, error } = await supabase
      .from('user_followers')
      .select('follower_id, created_at')
      .eq('followed_id', targetUserId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user_followers:', error);
      return [];
    }

    const followerIds = Array.from(new Set((followRows || []).map((r: any) => Number(r.follower_id)).filter(Boolean)));
    if (followerIds.length === 0) return [];

    // Hidrata com dados seguros de usuários
    let usersList: any[] = [];
    try {
      const { data: safeUsers, error: safeErr } = await (supabase.rpc('get_safe_users') as any)
        .select('*')
        .in('id', followerIds);
      if (!safeErr && safeUsers && safeUsers.length > 0) {
        usersList = safeUsers;
      }
    } catch (e) {
      console.warn('Fallback get_safe_users for followers:', e);
    }

    if (usersList.length === 0) {
      const { data: rawUsers } = await supabase
        .from('users')
        .select('id, username, fullname, avatar, bio, fighter_tags, role_id')
        .in('id', followerIds);
      usersList = rawUsers || [];
    }

    let storeMap: Record<number, string> = {};
    try {
      const { data: storesData } = await supabase
        .from('stores')
        .select('logo, store_members!inner(user_id)')
        .in('store_members.user_id', followerIds);

      (storesData || []).forEach((s: any) => {
        const member = Array.isArray(s.store_members) ? s.store_members[0] : s.store_members;
        if (member?.user_id) storeMap[Number(member.user_id)] = s.logo;
      });
    } catch {
      // ignore
    }

    const userMap = new Map<number, any>(usersList.map((u: any) => [Number(u.id), u]));

    return (followRows || [])
      .map((r: any) => {
        const u = userMap.get(Number(r.follower_id));
        if (!u) return null;
        return {
          ...u,
          followed_at: r.created_at,
          store_logo: storeMap[Number(u.id)] || null
        };
      })
      .filter(Boolean);
  } catch (err) {
    console.error('Error in getUserFollowers:', err);
    return [];
  }
};

/**
 * Retorna os usuários que um determinado usuário segue
 */
export const getUserFollowing = async (idOrUsername: string | number): Promise<any[]> => {
  try {
    const targetUserId = await resolveTargetId(idOrUsername);
    if (!targetUserId) return [];

    const { data: followRows, error } = await supabase
      .from('user_followers')
      .select('followed_id, created_at')
      .eq('follower_id', targetUserId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user_following:', error);
      return [];
    }

    const followedIds = Array.from(new Set((followRows || []).map((r: any) => Number(r.followed_id)).filter(Boolean)));
    if (followedIds.length === 0) return [];

    let usersList: any[] = [];
    try {
      const { data: safeUsers, error: safeErr } = await (supabase.rpc('get_safe_users') as any)
        .select('*')
        .in('id', followedIds);
      if (!safeErr && safeUsers && safeUsers.length > 0) {
        usersList = safeUsers;
      }
    } catch (e) {
      console.warn('Fallback get_safe_users for following:', e);
    }

    if (usersList.length === 0) {
      const { data: rawUsers } = await supabase
        .from('users')
        .select('id, username, fullname, avatar, bio, fighter_tags, role_id')
        .in('id', followedIds);
      usersList = rawUsers || [];
    }

    let storeMap: Record<number, string> = {};
    try {
      const { data: storesData } = await supabase
        .from('stores')
        .select('logo, store_members!inner(user_id)')
        .in('store_members.user_id', followedIds);

      (storesData || []).forEach((s: any) => {
        const member = Array.isArray(s.store_members) ? s.store_members[0] : s.store_members;
        if (member?.user_id) storeMap[Number(member.user_id)] = s.logo;
      });
    } catch {
      // ignore
    }

    const userMap = new Map<number, any>(usersList.map((u: any) => [Number(u.id), u]));

    return (followRows || [])
      .map((r: any) => {
        const u = userMap.get(Number(r.followed_id));
        if (!u) return null;
        return {
          ...u,
          followed_at: r.created_at,
          store_logo: storeMap[Number(u.id)] || null
        };
      })
      .filter(Boolean);
  } catch (err) {
    console.error('Error in getUserFollowing:', err);
    return [];
  }
};

/**
 * Retorna um Set com os IDs dos usuários que o currentUserId segue
 */
export const getMyFollowingIds = async (currentUserId: string | number): Promise<Set<number>> => {
  if (!currentUserId) return new Set();
  try {
    const { data } = await supabase
      .from('user_followers')
      .select('followed_id')
      .eq('follower_id', Number(currentUserId));
    return new Set((data || []).map((r: any) => Number(r.followed_id)));
  } catch {
    return new Set();
  }
};
